/**
 * Sub-tab "Histórico" do Controle PLD (Task #190 Fase 2; rico em #226/#29).
 *
 * Toggle MENSAL/ANUAL + filtro de RANGE. Gráfico de 4 séries (SE/S/NE/N) +
 * tabela, do mais recente pro mais antigo. Fonte:
 *  - Mensal: /api/v2/pld/mensal (canonical_pld_mensal, PLD realizado publicado,
 *    2001+). Sem coluna "Dias" — é o mensal autoritativo (≠ média diária do
 *    /historico, que era 2025+).
 *  - Anual: /api/v2/pld/anual (média aritmética dos meses; ano parcial marcado).
 * Realizado puro, SEM DECOMP (DECOMP é semanal — visão própria, PR3).
 */
import { useMemo, useState } from "react";
import type { PldSubmercado } from "../../../api/types";
import { usePldAnualAll } from "../../../hooks/usePldAnual";
import { usePldMensalAll } from "../../../hooks/usePldMensal";
import { usePldHistoricoDecomp } from "../../../hooks/usePldHistoricoDecomp";
import { PldLineChart, type PldChartPoint } from "./PldLineChart";
import { PldEmpty, PldError, PldLoading } from "./PldStates";
import {
  SUBMERCADO_COLOR,
  SUBMERCADO_LABEL,
  SUBMERCADOS,
  classBySign,
  fmtDif,
  fmtPldNum,
  mesLabel,
} from "./pldShared";

type Granularidade = "mensal" | "anual";
// null = "Tudo" (sem corte). Mensal em meses; anual em anos.
const RANGES_MENSAL: { label: string; n: number | null }[] = [
  { label: "12m", n: 12 },
  { label: "24m", n: 24 },
  { label: "36m", n: 36 },
  { label: "Tudo", n: null },
];
const RANGES_ANUAL: { label: string; n: number | null }[] = [
  { label: "10a", n: 10 },
  { label: "Tudo", n: null },
];

function pill(active: boolean): string {
  return [
    "text-[11px] font-semibold rounded-md px-2.5 py-1 border transition-colors",
    active
      ? "bg-indra-turquesa/20 text-indra-turquesa border-indra-turquesa/40"
      : "text-white/50 border-white/10 hover:text-white/80",
  ].join(" ");
}

export function PldHistoricoTab() {
  const [gran, setGran] = useState<Granularidade>("mensal");
  const [rangeMensal, setRangeMensal] = useState<number | null>(12);
  const [rangeAnual, setRangeAnual] = useState<number | null>(null);
  // #29 PR3: submercado selecionado pra visão Realizado × DECOMP × Δ.
  const [subDecomp, setSubDecomp] = useState<PldSubmercado>("SE");

  // Ambos pré-carregados (cache 5min) → toggle instantâneo.
  const mensal = usePldMensalAll();
  const anual = usePldAnualAll();
  // #29 PR3: histórico DECOMP do submercado selecionado (realizado × decomp × Δ).
  const hd = usePldHistoricoDecomp(subDecomp);
  const hdMensalIdx = useMemo(
    () => new Map((hd.data?.meses ?? []).map((m) => [m.mes_ref, m])),
    [hd.data],
  );
  const hdAnualIdx = useMemo(
    () => new Map((hd.data?.anos ?? []).map((a) => [a.ano, a])),
    [hd.data],
  );

  // Espinha = série do SE; corta pelo range (mais recentes).
  const mensalSpine = useMemo(() => {
    const all = (mensal.bySubmercado.SE?.meses ?? []).map((m) => m.mes_ref);
    return rangeMensal == null ? all : all.slice(-rangeMensal);
  }, [mensal.bySubmercado, rangeMensal]);

  const anualSpine = useMemo(() => {
    const all = (anual.bySubmercado.SE?.anos ?? []).map((a) => a.ano);
    return rangeAnual == null ? all : all.slice(-rangeAnual);
  }, [anual.bySubmercado, rangeAnual]);

  // Índices por mes_ref/ano por submercado (lookup O(1)).
  const mensalIdx = useMemo(() => {
    const idx = {} as Record<PldSubmercado, Map<string, number>>;
    for (const sub of SUBMERCADOS) {
      idx[sub] = new Map((mensal.bySubmercado[sub]?.meses ?? []).map((m) => [m.mes_ref, m.pld_realizado]));
    }
    return idx;
  }, [mensal.bySubmercado]);

  const anualIdx = useMemo(() => {
    const val = {} as Record<PldSubmercado, Map<number, number>>;
    const comp = new Map<number, boolean>();
    for (const sub of SUBMERCADOS) {
      val[sub] = new Map((anual.bySubmercado[sub]?.anos ?? []).map((a) => [a.ano, a.pld_medio]));
    }
    for (const a of anual.bySubmercado.SE?.anos ?? []) comp.set(a.ano, a.completo);
    return { val, comp };
  }, [anual.bySubmercado]);

  // Pontos do gráfico (ordem crescente) e linhas da tabela (decrescente).
  const pontos = useMemo<PldChartPoint[]>(() => {
    if (gran === "mensal") {
      return mensalSpine.map((mesRef) => {
        const row: PldChartPoint = { x: mesLabel(mesRef) };
        for (const sub of SUBMERCADOS) row[sub] = mensalIdx[sub].get(mesRef) ?? null;
        return row;
      });
    }
    return anualSpine.map((ano) => {
      const row: PldChartPoint = { x: String(ano) };
      for (const sub of SUBMERCADOS) row[sub] = anualIdx.val[sub].get(ano) ?? null;
      return row;
    });
  }, [gran, mensalSpine, anualSpine, mensalIdx, anualIdx]);

  const linhas = useMemo(() => {
    if (gran === "mensal") {
      return [...mensalSpine].reverse().map((mesRef) => {
        const valores = {} as Record<PldSubmercado, number | null>;
        for (const sub of SUBMERCADOS) valores[sub] = mensalIdx[sub].get(mesRef) ?? null;
        return { key: mesRef, label: mesLabel(mesRef), valores, parcial: false };
      });
    }
    return [...anualSpine].reverse().map((ano) => {
      const valores = {} as Record<PldSubmercado, number | null>;
      for (const sub of SUBMERCADOS) valores[sub] = anualIdx.val[sub].get(ano) ?? null;
      const parcial = anualIdx.comp.get(ano) === false;
      return { key: String(ano), label: parcial ? `${ano}*` : String(ano), valores, parcial };
    });
  }, [gran, mensalSpine, anualSpine, mensalIdx, anualIdx]);

  const active = gran === "mensal" ? mensal : anual;
  const spineLen = gran === "mensal" ? mensalSpine.length : anualSpine.length;
  const temParcial = gran === "anual" && linhas.some((l) => l.parcial);

  if (active.error) return <PldError />;
  if (active.isLoading) return <PldLoading />;
  if (spineLen === 0) return <PldEmpty />;

  const ranges = gran === "mensal" ? RANGES_MENSAL : RANGES_ANUAL;
  const rangeAtual = gran === "mensal" ? rangeMensal : rangeAnual;
  const setRange = gran === "mensal" ? setRangeMensal : setRangeAnual;

  return (
    <div data-testid="pld-historico">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-white/50 text-xs">
          PLD médio {gran === "mensal" ? "mensal" : "anual"} realizado (R$/MWh) — 4 submercados.
          {gran === "mensal"
            ? " Fonte: PLD publicado (2001+)."
            : " Média aritmética dos meses do ano (2001+)."}
          {temParcial && <span className="text-white/40"> · * = ano parcial.</span>}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex gap-1" role="group" aria-label="Granularidade">
            <button type="button" data-testid="pld-hist-gran-mensal"
              className={pill(gran === "mensal")} onClick={() => setGran("mensal")}>Mensal</button>
            <button type="button" data-testid="pld-hist-gran-anual"
              className={pill(gran === "anual")} onClick={() => setGran("anual")}>Anual</button>
          </div>
          <div className="flex gap-1" role="group" aria-label="Range">
            {ranges.map((r) => (
              <button key={r.label} type="button"
                data-testid={`pld-hist-range-${r.label}`}
                className={pill(rangeAtual === r.n)} onClick={() => setRange(r.n)}>{r.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <PldLineChart
          points={pontos}
          xKey="x"
          series={SUBMERCADOS.map((sub) => ({
            key: sub,
            label: SUBMERCADO_LABEL[sub],
            color: SUBMERCADO_COLOR[sub],
          }))}
          testId="pld-historico-chart"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{gran === "mensal" ? "Mês" : "Ano"}</th>
              {SUBMERCADOS.map((sub) => (
                <th key={sub} className="px-3 py-2 text-right">{sub}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr
                key={l.key}
                className="border-t border-white/5 hover:bg-white/5"
                data-testid="pld-hist-row"
              >
                <td className="px-3 py-2 text-white/90 font-semibold">
                  {l.label}
                  {l.parcial && (
                    <span className="text-white/40 font-normal" title="Ano parcial (meses incompletos)"> parcial</span>
                  )}
                </td>
                {SUBMERCADOS.map((sub) => (
                  <td key={sub} className="px-3 py-2 text-right font-mono text-white/80">
                    {fmtPldNum(l.valores[sub])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* #29 PR3: Realizado × DECOMP × Δ (Dessem×DECOMP) por submercado, range
          longo. DECOMP = média das REVs do período; Δ = realizado − DECOMP.
          Submercado sem série DECOMP → DECOMP/Δ "—" (não inventa âncora). */}
      <div className="mt-8" data-testid="pld-hist-decomp">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <h3 className="text-white font-semibold text-sm">
              Realizado × DECOMP — Δ Dessem×DECOMP
            </h3>
            <p className="text-white/50 text-xs mt-1">
              {gran === "mensal" ? "Mensal" : "Anual"} · {SUBMERCADO_LABEL[subDecomp]} ·
              DECOMP = média das REVs do período (R$/MWh). Δ = realizado − DECOMP.
            </p>
          </div>
          <div className="flex gap-1" role="group" aria-label="Submercado DECOMP">
            {SUBMERCADOS.map((sub) => (
              <button
                key={sub}
                type="button"
                data-testid={`pld-hist-decomp-sub-${sub}`}
                className={pill(subDecomp === sub)}
                onClick={() => setSubDecomp(sub)}
              >
                {sub}
              </button>
            ))}
          </div>
        </div>

        {hd.error ? (
          <p className="text-white/40 text-xs">Erro ao carregar DECOMP.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Período</th>
                  <th className="px-3 py-2 text-right">Realizado</th>
                  <th className="px-3 py-2 text-right">DECOMP</th>
                  <th
                    className="px-3 py-2 text-right"
                    title="Δ = PLD realizado − DECOMP (média das REVs). Negativo = realizado abaixo da DECOMP; positivo = acima."
                  >
                    Δ Dessem×DECOMP
                  </th>
                </tr>
              </thead>
              <tbody>
                {(gran === "mensal"
                  ? [...mensalSpine].reverse().map((mesRef) => ({
                      key: mesRef, label: mesLabel(mesRef), item: hdMensalIdx.get(mesRef),
                    }))
                  : [...anualSpine].reverse().map((ano) => ({
                      key: String(ano), label: String(ano), item: hdAnualIdx.get(ano),
                    }))
                ).map(({ key, label, item }) => (
                  <tr
                    key={key}
                    className="border-t border-white/5 hover:bg-white/5"
                    data-testid="pld-hist-decomp-row"
                  >
                    <td className="px-3 py-2 text-white/90 font-semibold">{label}</td>
                    <td className="px-3 py-2 text-right font-mono text-white/80">
                      {fmtPldNum(item?.realizado ?? null)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-white/80">
                      {fmtPldNum(item?.decomp ?? null)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        item?.delta == null ? "text-white/30" : classBySign(item.delta)
                      }`}
                      data-testid="pld-hist-decomp-delta"
                    >
                      {fmtDif(item?.delta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
